import { doPosition, Placement, Pos } from '@cmps/cdk';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import * as ReactDOM from 'react-dom';

interface TooltipProps {
    content: React.ReactNode | string;
    placement?: Placement;
    targetRef: React.RefObject<HTMLDivElement>;
    isVisible: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({
    content,
    placement = 'top-center',
    targetRef,
    isVisible,
}): JSX.Element => {
    const [position, setPosition] = useState<Pos>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const updatePosition = (): void => {
        if (targetRef?.current == null || tooltipRef?.current == null) {
            return;
        }

        const pos = doPosition(
            tooltipRef.current,
            targetRef.current,
            placement,
        );

        setPosition(pos);
    };

    useEffect(() => {
        tooltipRef?.current
            ?.querySelector('.cmps-tooltip')
            ?.setAttribute('placement', position?.placement ?? placement);

        if (position?.style != null) {
            Object.keys(position.style).forEach((k) => {
                tooltipRef.current.style[k] = position.style[k];
            });
        }

        if (isVisible) {
            updatePosition();
        } else {
            setPosition(null);
        }
    }, [tooltipRef, targetRef, position, placement, isVisible]);

    const tooltipElement = (
        <div
            className="cmps-tooltip-trigger--portal"
            ref={tooltipRef}
        >
            <div className="cmps-tooltip">
                <div className="cmps-tooltip--content">
                    {typeof content === 'string' ? (
                        <span>{content}</span>
                    ) : (
                        content
                    )}
                </div>
                <div className="cmps-tooltip--arrow-wrapper">
                    <div className="cmps-tooltip--arrow"></div>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(tooltipElement, document.body);
};

export default Tooltip;
